import { defineStore } from 'pinia';

/** 关闭福利弹窗后，未发生充值操作则再次弹出 */
export const DEPOSIT_BONUS_REMIND_MS = 3 * 60 * 1000;

let remindTimer = 0;
let eligibilityRequest = null;

function parseDepositAmount(conditions) {
  const raw = conditions?.depositAmount;
  if (raw == null || raw === '') return null;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

function clearRemindTimer() {
  if (remindTimer && typeof window !== 'undefined') {
    window.clearTimeout(remindTimer);
  }
  remindTimer = 0;
}

/**
 * 充值赠送福利弹窗：游客关闭注册奖励弹窗后展示一次；登录后关闭则 3 分钟未充值再提醒。
 */
export const useDepositBonusPromptStore = defineStore('depositBonusPrompt', {
  state: () => ({
    dialogOpen: false,
    /** 本次登录会话内可自动弹出 / 提醒 */
    sessionActive: false,
    /** 登录后尚未完成首次自动弹出 */
    armed: false,
    /** 游客关闭注册奖励弹窗后，尚未完成一次活动展示 */
    guestArmed: false,
    /** 当前游客会话已展示过活动弹窗 */
    guestShown: false,
    /** 3 分钟到点，等前置弹窗让出后再弹 */
    pendingRemind: false,
    /** 已发生充值操作（下单 / 成功），不再自动弹 */
    acted: false,
    /** 仅确认累计真实充值为 0 的用户展示活动 */
    campaignEligible: false,
    eligibilityResolved: false,
    eligibilityLoading: false,
    eligibilityError: '',
    eligibilityGeneration: 0,
  }),

  actions: {
    resetOnLogout() {
      this.dialogOpen = false;
      this.sessionActive = false;
      this.armed = false;
      this.guestArmed = false;
      this.guestShown = false;
      this.pendingRemind = false;
      this.acted = false;
      this.campaignEligible = false;
      this.eligibilityResolved = false;
      this.eligibilityLoading = false;
      this.eligibilityError = '';
      this.eligibilityGeneration += 1;
      eligibilityRequest = null;
      clearRemindTimer();
    },

    applyWithdrawConditions(conditions) {
      const depositAmount = parseDepositAmount(conditions);
      if (depositAmount == null) return null;
      this.campaignEligible = depositAmount === 0;
      this.eligibilityResolved = true;
      this.eligibilityError = '';
      if (!this.campaignEligible) this.markActed();
      return this.campaignEligible;
    },

    async ensureEligibility(knownConditions = null) {
      const knownResult = this.applyWithdrawConditions(knownConditions);
      if (knownResult != null) return knownResult;
      if (this.eligibilityResolved) return this.campaignEligible;
      if (eligibilityRequest) return eligibilityRequest;

      const generation = this.eligibilityGeneration;
      this.eligibilityLoading = true;
      const request = import('@/api/trial-fund')
        .then(({ getTrialWithdrawConditions }) => getTrialWithdrawConditions())
        .then((conditions) => {
          if (generation !== this.eligibilityGeneration) return false;
          const eligible = this.applyWithdrawConditions(conditions);
          if (eligible == null) throw new Error('missing depositAmount');
          return eligible;
        })
        .catch((err) => {
          if (generation === this.eligibilityGeneration) {
            this.campaignEligible = false;
            this.eligibilityError = String(err?.message ?? 'eligibility_failed');
          }
          return false;
        })
        .finally(() => {
          if (eligibilityRequest === request) eligibilityRequest = null;
          if (generation === this.eligibilityGeneration) this.eligibilityLoading = false;
        });
      eligibilityRequest = request;
      return request;
    },

    /** 登录成功后武装；等前置弹窗结束再 tryShow */
    armAfterLogin() {
      if (!this.campaignEligible) return false;
      this.sessionActive = true;
      this.armed = true;
      this.pendingRemind = false;
      this.acted = false;
      this.dialogOpen = false;
      clearRemindTimer();
      return true;
    },

    armGuestAfterRegisterRewardDismiss() {
      if (this.guestShown || this.dialogOpen) return false;
      this.guestArmed = true;
      return true;
    },

    tryShowGuest(opts = {}) {
      if (!this.guestArmed || this.guestShown || this.dialogOpen) return false;
      if (opts.blocked) return false;
      this.guestArmed = false;
      this.guestShown = true;
      this.dialogOpen = true;
      return true;
    },

    hideForQuietShell() {
      this.dialogOpen = false;
    },

    /** 活动条等手动打开 */
    openManual() {
      if (!this.campaignEligible) return false;
      this.dialogOpen = true;
      this.pendingRemind = false;
      clearRemindTimer();
      return true;
    },

    /**
     * @param {{ blocked?: boolean }} [opts]
     */
    tryShow(opts = {}) {
      if (!this.campaignEligible || !this.sessionActive || this.acted || this.dialogOpen) {
        return false;
      }
      if (opts.blocked) return false;
      if (!this.armed && !this.pendingRemind) return false;
      this.armed = false;
      this.pendingRemind = false;
      this.dialogOpen = true;
      clearRemindTimer();
      return true;
    },

    /** 用户关掉弹窗（X / 遮罩）；未充值则预约 3 分钟提醒 */
    dismiss() {
      if (!this.dialogOpen) return;
      this.dialogOpen = false;
      this.scheduleRemind();
    },

    /** 「去充值」：关掉福利窗并预约提醒（打开充值弹窗本身不算充值操作）。 */
    closeBeforeDeposit() {
      this.dialogOpen = false;
      this.scheduleRemind();
    },

    scheduleRemind() {
      clearRemindTimer();
      if (!this.sessionActive || this.acted) return;
      if (typeof window === 'undefined') return;
      remindTimer = window.setTimeout(() => {
        remindTimer = 0;
        if (!this.sessionActive || this.acted || this.dialogOpen) return;
        this.pendingRemind = true;
      }, DEPOSIT_BONUS_REMIND_MS);
    },

    stopRemind() {
      clearRemindTimer();
    },

    markActed() {
      this.acted = true;
      this.armed = false;
      this.guestArmed = false;
      this.pendingRemind = false;
      this.dialogOpen = false;
      clearRemindTimer();
    },

    markSuccessfulDeposit() {
      this.eligibilityGeneration += 1;
      eligibilityRequest = null;
      this.campaignEligible = false;
      this.eligibilityResolved = true;
      this.eligibilityLoading = false;
      this.eligibilityError = '';
      this.markActed();
    },
  },
});

export function markDepositBonusActed() {
  try {
    useDepositBonusPromptStore().markActed();
  } catch {
    /* Pinia 未就绪 */
  }
}

export function markDepositBonusSuccessful() {
  try {
    useDepositBonusPromptStore().markSuccessfulDeposit();
  } catch {
    /* Pinia 未就绪 */
  }
}
